import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { User } from 'firebase/auth';

import { AuthContext } from '@/auth/AuthProvider';
import { useAuth } from '@/auth/useAuth';

import { StudentViewBanner } from './StudentViewBanner';
import { StudentViewResetDialog } from './StudentViewResetDialog';
import {
  clearStudentViewSession,
  initializeStudentViewSession,
  persistShowHiddenContent,
  type StudentViewSession,
} from './studentViewSession';

export interface StudentViewContextValue {
  active: boolean;
  courseId: string | null;
  userId: string | null;
  exitStudentView: () => void;
  /**
   * Display-only reveal of content that visibility settings hide from
   * students. Never changes any stored setting — pages consult it to render
   * hidden content, and the banner labels the state clearly.
   */
  showHiddenContent: boolean;
  setShowHiddenContent: (value: boolean) => void;
  /**
   * True when the CURRENT page determined that some of its content is hidden
   * from students; drives the banner's warning + "Show all content" action.
   * Pages report via reportHiddenContent (and clear on unmount).
   */
  hasHiddenContent: boolean;
  reportHiddenContent: (present: boolean) => void;
  /**
   * The component currently open (the content page reports it; the course home
   * does not). Drives the banner's per-component "Reset test data" action —
   * reset is only ever offered inside a single component.
   */
  reportComponent: (component: StudentViewComponent | null) => void;
}

export interface StudentViewComponent {
  id: string;
  title?: string;
}

const INACTIVE: StudentViewContextValue = {
  active: false,
  courseId: null,
  userId: null,
  exitStudentView: () => {},
  showHiddenContent: false,
  setShowHiddenContent: () => {},
  hasHiddenContent: false,
  reportHiddenContent: () => {},
  reportComponent: () => {},
};

export const StudentViewContext =
  createContext<StudentViewContextValue>(INACTIVE);

export function useStudentView(): StudentViewContextValue {
  return useContext(StudentViewContext);
}

/**
 * Mask the signed-in instructor as their student-view test account. Identity
 * for every Kudu API call flows from getKuduUserId(user) — providerData[0].uid
 * first — and the legacy token is md5 of that id, so masking the user object
 * re-identifies EVERY course interaction (fetches, saves, checks, AI chats) as
 * the test student. The Firebase session itself is untouched: only this tab's
 * React context presents the masked identity (same pattern as the E2E injected
 * user).
 */
function maskStudentViewUser(realUser: User, testUserId: string): User {
  return {
    displayName: 'Test Student',
    email: testUserId,
    emailVerified: true,
    getIdToken: realUser.getIdToken.bind(realUser),
    photoURL: null,
    providerData: [{ uid: testUserId }],
    uid: testUserId,
  } as unknown as User;
}

interface StudentViewProviderProps {
  children: ReactNode;
}

/**
 * Activates student view for this TAB when the session (URL parameters on
 * first load, then sessionStorage) says so: re-provides the auth context with
 * the masked test-student identity and shows a persistent banner. Inactive
 * tabs render children untouched, so the instructor's own tabs never change.
 */
export function StudentViewProvider({ children }: StudentViewProviderProps) {
  const auth = useAuth();
  const [session] = useState<StudentViewSession | null>(() =>
    initializeStudentViewSession(),
  );
  const [showHiddenContent, setShowHiddenContentState] = useState(
    () => session?.showHiddenContent === true,
  );
  const [hasHiddenContent, setHasHiddenContent] = useState(false);
  const [activeComponent, setActiveComponent] =
    useState<StudentViewComponent | null>(null);
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);

  const exitStudentView = useCallback(() => {
    clearStudentViewSession();

    // The tab was opened by the instructor's own window; closing it is the
    // natural exit. If the browser refuses (e.g. the tab was reopened
    // manually), fall back to a full navigation, which remounts the app
    // without the session and lands on the instructor's course page.
    window.close();
    window.location.assign(
      session ? `/app/courses/${session.courseId}` : '/app/courses',
    );
  }, [session]);

  const setShowHiddenContent = useCallback((value: boolean) => {
    persistShowHiddenContent(value);
    setShowHiddenContentState(value);
  }, []);

  const reportHiddenContent = useCallback((present: boolean) => {
    setHasHiddenContent(present);
  }, []);

  const reportComponent = useCallback(
    (component: StudentViewComponent | null) => {
      setActiveComponent(component);
    },
    [],
  );

  const contextValue = useMemo<StudentViewContextValue>(() => {
    if (!session || !auth.user) {
      return INACTIVE;
    }

    return {
      active: true,
      courseId: session.courseId,
      userId: session.userId,
      exitStudentView,
      showHiddenContent,
      setShowHiddenContent,
      hasHiddenContent,
      reportHiddenContent,
      reportComponent,
    };
  }, [
    session,
    auth.user,
    exitStudentView,
    showHiddenContent,
    setShowHiddenContent,
    hasHiddenContent,
    reportHiddenContent,
    reportComponent,
  ]);

  const maskedAuth = useMemo(() => {
    if (!session || !auth.user) {
      return null;
    }

    return {
      ...auth,
      user: maskStudentViewUser(auth.user, session.userId),
    };
  }, [session, auth]);

  if (!contextValue.active || !maskedAuth) {
    return (
      <StudentViewContext.Provider value={INACTIVE}>
        {children}
      </StudentViewContext.Provider>
    );
  }

  return (
    <StudentViewContext.Provider value={contextValue}>
      <AuthContext.Provider value={maskedAuth}>
        <StudentViewBanner
          canResetComponent={activeComponent !== null}
          hasHiddenContent={hasHiddenContent}
          onExit={exitStudentView}
          onResetComponent={() => setIsResetDialogOpen(true)}
          onToggleHiddenContent={() => setShowHiddenContent(!showHiddenContent)}
          showHiddenContent={showHiddenContent}
        />
        {activeComponent && contextValue.courseId ? (
          <StudentViewResetDialog
            componentId={activeComponent.id}
            componentTitle={activeComponent.title}
            courseId={contextValue.courseId}
            onOpenChange={setIsResetDialogOpen}
            open={isResetDialogOpen}
          />
        ) : null}
        {children}
      </AuthContext.Provider>
    </StudentViewContext.Provider>
  );
}
